/**
 * Unified online detection: Capacitor Network on native, navigator.onLine fallback.
 */

import { isCapacitorNative } from '@/lib/capacitor-runtime';

let online = true;
let initialized = false;
const listeners = new Set<(isOnline: boolean) => void>();
let teardown: (() => void) | null = null;

function setOnline(next: boolean) {
  if (online === next) return;
  online = next;
  if (next) {
    void import('@/lib/offline-sync')
      .then(({ offlineSync }) => offlineSync.syncPendingRequests())
      .catch(() => {});
  }
  listeners.forEach((listener) => {
    try {
      listener(next);
    } catch {
      /* ignore */
    }
  });
}

export function isNetworkStatusInitialized(): boolean {
  return initialized;
}

export function getNetworkOnline(): boolean {
  return online;
}

export function subscribeNetworkStatus(listener: (isOnline: boolean) => void): () => void {
  listeners.add(listener);
  listener(online);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Call once when the authenticated shell mounts (browser + native).
 */
export function initNetworkStatus(): () => void {
  if (typeof window === 'undefined') return () => {};
  if (initialized) return teardown ?? (() => {});

  initialized = true;
  online = window.navigator.onLine;

  const cleanups: Array<() => void> = [];

  const onBrowserOnline = () => setOnline(true);
  const onBrowserOffline = () => setOnline(false);
  window.addEventListener('online', onBrowserOnline);
  window.addEventListener('offline', onBrowserOffline);
  cleanups.push(() => {
    window.removeEventListener('online', onBrowserOnline);
    window.removeEventListener('offline', onBrowserOffline);
  });

  if (isCapacitorNative()) {
    void import('@capacitor/network')
      .then(({ Network }) => {
        void Network.getStatus()
          .then((status) => setOnline(status.connected))
          .catch(() => {});

        const handle = Network.addListener('networkStatusChange', (status) => {
          setOnline(status.connected);
        });
        cleanups.push(() => {
          void handle.then((h) => h.remove()).catch(() => {});
        });
      })
      .catch(() => {
        /* plugin unavailable — keep navigator.onLine */
      });
  }

  teardown = () => {
    cleanups.forEach((fn) => fn());
    listeners.clear();
    initialized = false;
    teardown = null;
  };

  return teardown;
}
